import { useState, useEffect, useCallback } from "react";
import apiClient from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { History, X } from "lucide-react";

// Plain-Arabic labels so non-technical users understand every row.
const MEASURE_ROWS = [
  { key: "ucva", label: "النظر بدون نظارة" },
  { key: "sph", label: "قياس العدسة SPH" },
  { key: "cyl", label: "الانحراف CYL" },
  { key: "ax", label: "محور الانحراف AX" },
  { key: "bcva", label: "النظر مع النظارة" },
  { key: "near", label: "النظر القريب" },
  { key: "iop", label: "ضغط العين" },
];
const ANATOMY_ROWS = [
  { key: "lid", label: "الجفن" },
  { key: "cornea", label: "القرنية" },
  { key: "lens", label: "العدسة" },
  { key: "retina", label: "الشبكية" },
];

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ar-EG", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
};

const val = (v) => (v || "").trim();

/** One visit rendered as a "paper" card. `other` is the visit on the
 * opposite side — any field whose value differs from it gets highlighted,
 * so differences pop out visually without any reading effort. */
const VisitPaper = ({ visit, other, title }) => {
  const diff = (a, b) => val(a) !== val(b);
  const cell = (mine, theirs, extra = "") => (
    <td className={`px-2 py-1.5 text-center text-sm ${extra} ${
      diff(mine, theirs)
        ? "bg-amber-100 dark:bg-amber-500/20 font-bold text-amber-900 dark:text-amber-200 rounded"
        : "text-[#1F2937] dark:text-ink-100"
    }`}>
      {val(mine) || "—"}
    </td>
  );

  const textBlock = (label, mine, theirs, tone = "") => (
    <div className="mt-3">
      <p className="text-xs font-bold text-slate-500 dark:text-ink-300 mb-1">{label}</p>
      <div className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap min-h-[38px] ${
        diff(mine, theirs)
          ? "bg-amber-50 dark:bg-amber-500/15 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100"
          : `bg-slate-50 dark:bg-ink-800/60 text-[#1F2937] dark:text-ink-100 ${tone}`
      }`}>
        {val(mine) || <span className="text-slate-400 dark:text-ink-400">فارغ</span>}
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-w-[300px] rounded-2xl border-2 border-slate-200 dark:border-ink-600 bg-white dark:bg-ink-900 shadow-sm p-4">
      <p className="text-center font-bold text-[#5B3A7D] dark:text-violet-300 mb-3 text-base">{title}</p>

      <p className="text-xs font-bold text-slate-500 dark:text-ink-300 mb-1">قياسات النظر</p>
      <table className="w-full border border-slate-200 dark:border-ink-700 rounded-lg overflow-hidden text-sm">
        <thead>
          <tr className="bg-[#5B3A7D] text-white text-xs">
            <th className="px-2 py-1.5 text-right">القياس</th>
            <th className="px-2 py-1.5">يمنى</th>
            <th className="px-2 py-1.5">يسرى</th>
          </tr>
        </thead>
        <tbody>
          {MEASURE_ROWS.map(({ key, label }, i) => (
            <tr key={key} className={i % 2 ? "bg-white dark:bg-ink-900" : "bg-slate-50 dark:bg-ink-800/50"}>
              <td className="px-2 py-1.5 text-xs text-slate-600 dark:text-ink-200">{label}</td>
              {cell(visit?.right_eye?.[key], other?.right_eye?.[key])}
              {cell(visit?.left_eye?.[key], other?.left_eye?.[key])}
            </tr>
          ))}
          {ANATOMY_ROWS.map(({ key, label }, i) => (
            <tr key={key} className={(MEASURE_ROWS.length + i) % 2 ? "bg-white dark:bg-ink-900" : "bg-slate-50 dark:bg-ink-800/50"}>
              <td className="px-2 py-1.5 text-xs text-slate-600 dark:text-ink-200">{label}</td>
              {cell(visit?.right_eye?.[key], other?.right_eye?.[key])}
              {cell(visit?.left_eye?.[key], other?.left_eye?.[key])}
            </tr>
          ))}
        </tbody>
      </table>

      {textBlock("التشخيص", visit?.diagnosis, other?.diagnosis)}
      {textBlock("الوصفة الطبية", visit?.prescription, other?.prescription)}
      {textBlock("الملاحظات", visit?.notes, other?.notes)}
      {textBlock("ملاحظة السكرتيرة (داخلية)", visit?.secretary_note, other?.secretary_note)}
    </div>
  );
};

/**
 * Side-by-side visit comparison ("two papers"): each side has its own visit
 * picker, so the doctor can compare current-vs-previous OR any two older
 * visits against each other. Differing values are highlighted in amber on
 * both papers automatically.
 */
const HistoryComparison = ({ patientId, currentData }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visits, setVisits] = useState([]); // [{id:'current',...}, ...history]
  const [leftId, setLeftId] = useState("current");
  const [rightId, setRightId] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/patients/${patientId}/history`);
      const hist = data.history || [];
      // "current" entry uses the LIVE form data (includes unsaved edits),
      // labeled clearly; history entries come from the server.
      const all = [
        { ...(currentData || {}), id: "current", _label: "الزيارة الحالية" },
        ...hist.map((h) => ({ ...h, _label: `زيارة ${formatDate(h.updated_at || h.created_at)}` })),
      ];
      setVisits(all);
      setLeftId("current");
      setRightId(hist.length ? hist[0].id : null);
    } catch (e) {
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, currentData]);

  useEffect(() => {
    if (open) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  if (!patientId) return null;

  const left = visits.find((v) => v.id === leftId);
  const right = visits.find((v) => v.id === rightId);

  const picker = (value, onChange, excludeId, testid) => (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-10 w-full text-sm font-medium">
        <SelectValue placeholder="اختر زيارة" />
      </SelectTrigger>
      <SelectContent>
        {visits.filter((v) => v.id !== excludeId).map((v) => (
          <SelectItem key={v.id} value={v.id} className="text-sm">{v._label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="mt-4">
      {!open ? (
        <Button
          data-testid="open-history-comparison-button"
          variant="outline" size="sm"
          onClick={() => setOpen(true)}
          className="gap-2 text-[#5B3A7D] dark:text-violet-400 border-[#5B3A7D]/30 dark:border-violet-900 hover:bg-[#5B3A7D]/5"
        >
          <History className="w-4 h-4" />
          مقارنة بالزيارات السابقة
        </Button>
      ) : (
        <div className="rounded-2xl border border-slate-200 dark:border-ink-700 bg-slate-50 dark:bg-ink-950/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#5B3A7D] dark:text-violet-400" />
              <h3 className="text-base font-bold text-[#1F2937] dark:text-ink-50">مقارنة الزيارات — ورقة مقابل ورقة</h3>
            </div>
            <Button
              data-testid="close-history-comparison-button"
              variant="ghost" size="icon" className="h-9 w-9"
              onClick={() => setOpen(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-10 text-sm text-slate-400 dark:text-ink-300">جاري التحميل...</div>
          ) : visits.length <= 1 ? (
            <div className="text-center py-10 text-sm text-slate-400 dark:text-ink-300">
              هذه أول زيارة مسجّلة لهذا المريض — لا يوجد ما يُقارن به بعد
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 dark:text-ink-300 mb-3">
                اختر أي زيارتين للمقارنة — القيم <span className="bg-amber-100 dark:bg-amber-500/20 text-amber-900 dark:text-amber-200 font-bold px-1.5 rounded">المظللة</span> هي التي تختلف بين الورقتين
              </p>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 min-w-[300px] space-y-2">
                  {picker(leftId, setLeftId, rightId, "compare-left-select")}
                  {left && <VisitPaper visit={left} other={right} title={left._label} />}
                </div>
                <div className="flex-1 min-w-[300px] space-y-2">
                  {picker(rightId, setRightId, leftId, "compare-right-select")}
                  {right && <VisitPaper visit={right} other={left} title={right._label} />}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryComparison;
